import { format } from 'date-fns';
import { isNull, reduce } from 'lodash';

import { withTitleCasedDrugFields } from 'common/utils/titleCase';
import graphqlClient from '../graphql';
import {
  associateCatalogProductGql,
  fillHistoryUpdateReportsGql,
  getActiveLineItemLabelGql,
  getFillHistoryUpdateReportDownloadUrlGql,
  getPrescriptionFillHistoryGql,
  getPrescriptionOverrideAuditsGql,
  getPrescriptionReviewDataGql,
  getPrescriptionSignedUrlGql,
  getRxByScanIdGql,
  listPrescriptionForReviewGql,
  listSigsGql,
  multiRxLineItemGql,
  overridePrescriptionGql,
  prescriptionByNdcGql,
  resolveDrugProfileGql,
  resolveDuplicatePrescriptionGql,
  resolveShipLaterReviewGql,
  syncSigGql,
  updateBulkFillHistoryGql,
  updateFillHistoryGql,
} from './gql';
import {
  transformGetPrescriptionData,
  transformListOverrideDetails,
  transformListPrescriptionsForReview,
} from './transform';

export const listSigsApi = ({ searchText }) =>
  graphqlClient.request(listSigsGql, { searchText }).then(({ listSigs }) => listSigs);

export const syncSigApi = (syncSigPayload) =>
  graphqlClient.request(syncSigGql, syncSigPayload).then(({ syncSig }) => syncSig);

export const listPrescriptionsForReviewApi = ({ page, sortBy, filter }) => {
  const transformedFilter = {
    ...filter,
    requestedDate: filter.requestedDate && format(new Date(filter.requestedDate), 'yyyy-MM-dd'),
    ...(filter?.partnerId && { partnerId: [filter.partnerId] }),
    ...(filter?.practiceId && { practiceId: [filter.practiceId] }),
  };

  return graphqlClient
    .request(listPrescriptionForReviewGql, {
      offset: (page - 1) * 25,
      sortBy,
      filter: reduce(
        transformedFilter,
        (result, value, key) => ({
          ...result,
          [key]: isNull(value) ? undefined : value,
        }),
        {}
      ),
    })
    .then(transformListPrescriptionsForReview);
};

export const resolveDuplicatePrescriptionApi = ({
  prescriptionId,
  orderId,
  isDuplicate,
  lineItemId,
}) => {
  return graphqlClient
    .request(resolveDuplicatePrescriptionGql, { prescriptionId, orderId, isDuplicate, lineItemId })
    .then(({ resolveDuplicatePrescriptionReview }) => resolveDuplicatePrescriptionReview);
};

export const resolveShipLaterReviewApi = ({ orderId, lineItemId, patientId, targetState }) => {
  return graphqlClient
    .request(resolveShipLaterReviewGql, { orderId, lineItemId, patientId, targetState })
    .then(({ resolveShipLaterReview }) => resolveShipLaterReview);
};

export const resolveDrugProfileApi = (input) => {
  return graphqlClient
    .request(resolveDrugProfileGql, { ...input })
    .then(({ resolveDrugProfile }) => resolveDrugProfile);
};

export const associateCatalogProductApi = ({
  prescriptionId,
  lineItemId,
  productId,
  hasOneTimeAssociation,
}) => {
  return graphqlClient
    .request(associateCatalogProductGql, {
      prescriptionId,
      lineItemId,
      productId,
      hasOneTimeAssociation,
    })
    .then(({ associateCatalogProduct }) => associateCatalogProduct);
};

export const overridePrescriptionAPI = (input, fromWorkQueue, isPrescriptionEdit, isFillEdit) => {
  return graphqlClient
    .request(overridePrescriptionGql, { ...input, fromWorkQueue, isPrescriptionEdit, isFillEdit })
    .then(({ overridePrescription }) => overridePrescription);
};

export const getPrescriptionOverrideAuditsApi = (prescriptionUUID) => {
  return graphqlClient
    .request(getPrescriptionOverrideAuditsGql, { prescriptionUUID })
    .then(transformListOverrideDetails);
};

export const multiRxLineItemApi = (modifyItemPayload) => {
  return graphqlClient
    .request(multiRxLineItemGql, modifyItemPayload)
    .then(transformListOverrideDetails);
};
export const getPrescriptionSignedUrlApi = ({ prescriptionUUID, fileKeys }) => {
  return graphqlClient.request(getPrescriptionSignedUrlGql, {
    prescriptionUUID,
    fileKeys,
  });
};

export const getPrescriptionFillHistoryApi = ({ prescriptionId }) => {
  return graphqlClient
    .request(getPrescriptionFillHistoryGql, { prescriptionId })
    .then(({ getPrescriptionFillHistory }) => getPrescriptionFillHistory);
};

export const updateFillHistoryApi = ({ fillHistoryId, overRides }) => {
  return graphqlClient
    .request(updateFillHistoryGql, {
      input: { fillHistoryId, overRides },
    })
    .then(({ updateFillHistory }) => updateFillHistory);
};

export const getPrescriptionReviewDataApi = ({ lineItemId, prescriptionId, reviewType }) => {
  return graphqlClient
    .request(getPrescriptionReviewDataGql, { lineItemId, prescriptionId, reviewType })
    .then(({ getPrescriptionReviewData }) =>
      transformGetPrescriptionData(getPrescriptionReviewData)
    );
};

export const prescriptionByNdcApi = ({ ndc, fillDateFrom, fillDateTo, limit, offset }) => {
  return graphqlClient
    .request(prescriptionByNdcGql, { filter: { ndc, fillDateFrom, fillDateTo }, limit, offset })
    .then(({ prescriptionByNdc }) => prescriptionByNdc);
};

export const updateBulkFillHistoryApi = ({ input }) => {
  return graphqlClient
    .request(updateBulkFillHistoryGql, { input })
    .then(({ updateBulkFillHistory }) => updateBulkFillHistory);
};

export const fillHistoryUpdateReportApi = ({ limit, offset, filter }) => {
  return graphqlClient
    .request(fillHistoryUpdateReportsGql, {
      limit,
      offset,
      filter,
      sortBy: {
        sortKey: 'lastModified',
        sortOrder: 'DESC',
      },
    })
    .then(({ fillHistoryUpdateReports }) => fillHistoryUpdateReports);
};

export const getFillHistoryUpdateReportDownloadUrlApi = ({ fileKey }) => {
  return graphqlClient
    .request(getFillHistoryUpdateReportDownloadUrlGql, { fileKey })
    .then(({ getFillHistoryUpdateReportDownloadUrl }) => getFillHistoryUpdateReportDownloadUrl);
};

export const getActiveLineItemLabelApi = ({ lineItemId }) =>
  graphqlClient
    .request(getActiveLineItemLabelGql, { lineItemId })
    .then(({ getActiveLineItemLabel }) => getActiveLineItemLabel ?? null);

export const getRxByScanIdApi = ({ sId }) =>
  graphqlClient
    .request(getRxByScanIdGql, { sId })
    .then(({ getRxByScanId }) =>
      getRxByScanId ? withTitleCasedDrugFields(getRxByScanId) : getRxByScanId
    );
